import { useMemo, useState } from 'react';
import { cn } from '@/utils/styles';
import { ShopTestsPage } from './ShopTestsPage';
import {
  ShopFormulasPage,
  ShopFormulasPageProps,
} from '@screens/ShopPage/ShopFormulasPage';
import { useHasRecommendation } from '@hooks/useHasRecommendation';
import { ShopNoRnrPage } from '@screens/ShopPage/components/ShopNoRnrPage';
import { useGetAllFormulasQuery } from '@viome/viome-base/src/usecase/Shop/ShopQuery';
import { KEYS, LocalStorage } from '@utils/LocalStorage.ts';
import { ErrorLoadingPage } from '@screens/ErrorHandling/ErrorLoadingPage';
import { ShopLandingPageSkeleton } from './ShopLandingPageSkeleton';
import { EventTracker } from '@/utils';
import { KitType } from '@viome/viome-base/src/usecase/Bootup/BootupModel';
import { ShopUnavailablePage } from '@screens/ShopPage/components/ShopUnavailablePage';

type Tab = 'formulas' | 'tests';

const TABS: { id: Tab; label: string }[] = [
  { id: 'formulas', label: 'My Formulas' },
  { id: 'tests', label: 'Tests' },
];

export const ShopLandingPage = () => {
  const { data: productData, isLoading, isError } = useGetAllFormulasQuery();
  const localStorageUser = LocalStorage.getItem(KEYS.BOOTUP);
  const { isEligibleForSupplements, kitType, preRnRKitType } =
    localStorageUser && JSON.parse(localStorageUser);

  const isPhproUser = 
    kitType === KitType.PHPRO || 
    preRnRKitType === KitType.PHPRO;

  const visibleTabs = useMemo(() => {
    if (isPhproUser) {
      return TABS.filter((tab) => tab.id === 'formulas');
    }

    if (!isEligibleForSupplements) {
      return TABS.filter((tab) => tab.id === 'tests');
    }
    
    return TABS;
  }, [isPhproUser, isEligibleForSupplements]);

  const isNoRnrUser = useHasRecommendation();
  const defaultTab = useMemo(() => {
    if (!productData) {
      return undefined;
    }

    if (isPhproUser) {
      return 'formulas';
    }
    
    const allProductsSubscribed =
      productData.length > 0 &&
      productData.every((formula) => formula.is_subscribed);
    return allProductsSubscribed || !isEligibleForSupplements
      ? 'tests'
      : 'formulas';
  }, [productData, isEligibleForSupplements, isPhproUser]);

  const [userSelectedTab, setUserSelectedTab] = useState<Tab>();

  const activeTab = userSelectedTab || defaultTab;
  const [showTabs, setShowTabs] = useState(true);

  const handleCustomizeModeChange: ShopFormulasPageProps['onCustomizeModeChange'] =
    (isCustomizing) => {
      setShowTabs(!isCustomizing);
    };

  if (isLoading) {
    return <ShopLandingPageSkeleton />;
  }

  if (isError) {
    return (
      <ErrorLoadingPage
        variant="light"
        errorType="error"
        hideBackground
        hideNavBar
      />
    );
  }

  return (
    <div className="page-container mx-auto px-4 py-6" data-testid="shopLanding-page">
      {showTabs && (
        <div className="flex justify-center mb-8" data-testid="shopLanding-tabs">
          <div className="flex rounded-full" data-testid="shopLanding-tabsContainer">
            {(defaultTab === 'tests'
              ? [...visibleTabs].reverse()
              : visibleTabs
            ).map((tab) => (
              <button
                key={tab.id}
                className={cn(
                  'py-2 px-6 rounded-full typography-btn-medium transition-colors',
                  activeTab === tab.id
                    ? 'bg-white text-primary shadow-sm'
                    : 'text-secondary hover:text-primary',
                )}
                onClick={() => {
                  EventTracker.trackEvent(
                    'shop',
                    `tap_shop_home_maintab_my${tab.id}`,
                  );
                  setUserSelectedTab(tab?.id);
                }}
                data-testid={`shopLanding-tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {isPhproUser && !isEligibleForSupplements ? (
        <ShopUnavailablePage tab="formulas" />
      ) : !isEligibleForSupplements || activeTab === 'tests' ? (
        <ShopTestsPage />
      ) : !isNoRnrUser ? (
        <ShopNoRnrPage />
      ) : (
        <ShopFormulasPage onCustomizeModeChange={handleCustomizeModeChange} />
      )}
    </div>
  );
};
